import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, auth } from "../lib/api";
import { toast } from "sonner";
import Nav from "../components/Nav";
import { Lock, ArrowRight, Compass } from "@phosphor-icons/react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      const r = await api.post("/auth/login", { email, password: pw });
      if (!r?.data?.token) throw new Error("no-token");
      auth.login(r.data.token, r.data.email);
      toast.success("Welcome back, advisor.");
      nav("/dashboard");
    } catch (e2) {
      const status = e2?.response?.status;
      const detail = e2?.response?.data?.detail;
      // Give reviewers + real users a clear next step. NEVER just "Invalid credentials".
      if (status === 401 || status === 403 || detail === "invalid credentials") {
        setErr(
          "That looks like a client account, not an advisor login. Advisors sign in here. Everyone else, tap Explore the app below to browse the tools with no login required."
        );
      } else if (!navigator.onLine) {
        setErr("You appear to be offline. Reconnect and try again.");
      } else if (status >= 500 || !status) {
        setErr("We couldn’t reach the server. Please try again in a moment.");
      } else {
        setErr(detail || "Sign-in failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-screen bg-exodus-bg text-exodus-text">
      <Nav />
      <div className="min-h-screen flex items-center justify-center pt-20 px-6">
        <form onSubmit={submit} data-testid="login-form" className="card-surface p-10 w-full max-w-md" autoComplete="off">
          <Lock size={32} weight="thin" className="text-exodus-gold mb-6" />
          <p className="eyebrow" data-testid="login-eyebrow">Advisor Portal · Staff Only</p>
          <h2 className="font-display text-3xl mt-3">Sign in to EXODUS.</h2>
          <p className="text-sm text-exodus-text/70 mt-3">
            This login is for licensed EXODUS advisors. Clients and visitors do not
            need an account to use the app — the quiz, calculator, learn library,
            audio playbooks, Wealth for Life Blueprint, and consult booking are all
            available with no sign-in.
          </p>
          {/* Decoy fields — iOS/Chrome autofill targets the FIRST username/password
              pair it can find. These hidden decoys absorb any keychain autofill
              so the real fields below stay clean. */}
          <input type="text" name="fakeusernameremembered" autoComplete="username" style={{ display: "none" }} />
          <input type="password" name="fakepasswordremembered" autoComplete="current-password" style={{ display: "none" }} />
          <div className="mt-8 space-y-4">
            <input
              data-testid="login-email"
              type="email"
              name="advisor_email_field"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              className="input-exodus"
              placeholder="Advisor email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <input
              data-testid="login-password"
              type="password"
              name="advisor_pw_field"
              autoComplete="new-password"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              className="input-exodus"
              placeholder="Password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              required
            />
            {err && (
              <p
                data-testid="login-error"
                className="text-sm text-red-300 bg-red-500/10 border border-red-500/40 rounded-md p-3 leading-relaxed"
              >
                {err}
              </p>
            )}
            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="btn-gold w-full justify-center"
            >
              {loading ? "Signing in…" : "Enter Advisor Dashboard"}
            </button>
          </div>

          {/* Guest / reviewer fallback — critical for Apple App Review approval */}
          <div className="mt-6 pt-6 border-t border-exodus-gold-deep/30 space-y-2">
            <p className="text-xs text-exodus-text/60 text-center">
              Not an advisor? You still have full access to the app.
            </p>
            <Link
              to="/"
              data-testid="login-explore-app"
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-exodus-gold text-exodus-gold hover:bg-exodus-gold hover:text-black transition text-sm font-semibold"
            >
              <Compass size={16} weight="fill" /> Explore the app
            </Link>
            <Link
              to="/quiz"
              data-testid="login-take-quiz"
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 text-sm text-exodus-text/80 hover:text-exodus-gold transition"
            >
              Or take the 90-second Wealth Quiz <ArrowRight size={14} weight="bold" />
            </Link>
          </div>

          <p className="text-[11px] text-exodus-muted mt-6 text-center leading-relaxed">
            EXODUS never sells or shares your data. See our{" "}
            <Link to="/privacy" className="underline hover:text-exodus-gold">Privacy Policy</Link>.
          </p>
        </form>
      </div>
    </div>
  );
}
